import React, { useEffect, useState } from 'react';
import {
  ExclamationTriangleIcon,
  CheckIcon,
  ComputerDesktopIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import {
  PasskeyError,
  isPlatformAuthenticatorAvailable,
  type PasskeyMethod,
} from '../lib/webauthn';
import { useLockContext } from '../contexts/LockContext';
import AuthLayout from './AuthLayout';
import { Button } from '@mes-amis/galette-design-system';

interface PasskeyRegistrationModalProps {
  /** Callback when registration is complete */
  onComplete: () => void;

  /** Callback when user cancels (only if not required) */
  onCancel: () => void;

  /** Whether registration is required (cannot be dismissed) */
  isRequired: boolean;
}

type RegistrationStep = 'intro' | 'registering' | 'success' | 'error';

interface MethodChoice {
  method: PasskeyMethod;
  title: string;
  description: string;
  Icon: typeof ComputerDesktopIcon;
}

const SECURITY_KEY_CHOICE: MethodChoice = {
  method: 'security-key',
  title: 'Use a security key',
  description: 'A USB or NFC FIDO2 key (e.g. YubiKey) with its own PIN or fingerprint.',
  Icon: KeyIcon,
};

const THIS_DEVICE_CHOICE: MethodChoice = {
  method: 'this-device',
  title: 'Use this device or a password manager',
  description: 'Windows Hello, Touch ID, or Face ID built into this computer, or a passkey saved in 1Password, Bitwarden, or a similar manager that integrates with this browser.',
  Icon: ComputerDesktopIcon,
};

/**
 * Modal for first-time passkey registration
 * Shows explanation, performs registration, and handles success/error states
 */
export default function PasskeyRegistrationModal({
  onComplete,
  onCancel,
  isRequired,
}: PasskeyRegistrationModalProps) {
  const { registerPasskey } = useLockContext();
  const [step, setStep] = useState<RegistrationStep>('intro');
  const [error, setError] = useState<string | null>(null);
  // null = still probing. Determines whether "Use this device" is offered —
  // on an Intune-managed Windows laptop with Hello disabled, the probe
  // returns false and we hide the option rather than letting the user pick
  // a path that will fail at the OS prompt.
  const [platformAvailable, setPlatformAvailable] = useState<boolean | null>(null);
  const [lastMethod, setLastMethod] = useState<PasskeyMethod | null>(null);

  useEffect(() => {
    let cancelled = false;
    isPlatformAuthenticatorAvailable().then((available) => {
      if (!cancelled) setPlatformAvailable(available);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleRegister = async (method: PasskeyMethod) => {
    setLastMethod(method);
    try {
      setStep('registering');
      setError(null);

      const success = await registerPasskey(method);

      if (success) {
        setStep('success');

        setTimeout(() => {
          onComplete();
        }, 2000);
      } else {
        setError(
          'Failed to register passkey authentication. Please try again.',
        );
        setStep('error');
      }
    } catch (error) {
      console.error('Passkey registration failed:', error);

      if (error instanceof PasskeyError) {
        setError(error.message);
      } else {
        setError(
          'Failed to register passkey authentication. Please try again.',
        );
      }

      setStep('error');
    }
  };

  const choices: MethodChoice[] = [
    ...(platformAvailable ? [THIS_DEVICE_CHOICE] : []),
    SECURITY_KEY_CHOICE,
  ];

  return (
    <AuthLayout className="z-50" maxWidth="max-w-lg">
      {/* Intro Step */}
      {step === 'intro' && (
        <>
          <div className="flex flex-col gap-4">
            <h1 className="font-heading text-[28px] leading-8 text-black">
              Setup Passkey Security
            </h1>
            <p className="text-sm leading-5 text-black">
              To protect sensitive client information, the app needs a passkey.
              Pick how you’d like to unlock the app:
            </p>
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              {platformAvailable === null ? (
                <div
                  className="flex items-center justify-center py-8"
                  role="status"
                  aria-label="Checking available authenticators"
                >
                  <div
                    className="animate-spin rounded-full h-6 w-6 border-b-2 border-deep-sea-green"
                    aria-hidden="true"
                  />
                </div>
              ) : (
                choices.map(({ method, title, description, Icon }) => (
                  <button
                    key={method}
                    type="button"
                    onClick={() => handleRegister(method)}
                    className="text-left flex items-start gap-3 p-3 border border-gray-200 rounded hover:border-deep-sea-green hover:bg-deep-sea-green/5 transition-colors"
                  >
                    <Icon className="h-6 w-6 text-deep-sea-green flex-shrink-0 mt-0.5" />
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-semibold text-black">{title}</span>
                      <span className="text-xs text-[#555]">{description}</span>
                    </div>
                  </button>
                ))
              )}
            </div>

            <div className="bg-yellow-50 border border-yellow-300 rounded p-4">
              <div className="flex gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-yellow-900 text-sm mb-1">
                    Important
                  </h3>
                  <p className="text-xs text-yellow-800">
                    If you lose access to your passkey, you will lose access to
                    all offline data. Make sure to sync changes regularly when
                    online to prevent data loss.
                  </p>
                </div>
              </div>
            </div>

            {!isRequired && (
              <button
                type="button"
                onClick={onCancel}
                className="text-sm font-medium text-[#555] hover:text-black transition-colors self-start"
              >
                Skip for Now
              </button>
            )}

            {isRequired && (
              <p className="text-xs text-[#555]">
                Passkey setup is required to access the application
              </p>
            )}
          </div>
        </>
      )}

      {/* Registering Step */}
      {step === 'registering' && (
        <>
          <div className="flex flex-col gap-4">
            <h1 className="font-heading text-[28px] leading-8 text-black">
              Setting Up Passkey
            </h1>
            <p className="text-sm leading-5 text-black">
              Please authenticate using your device&apos;s passkey...
            </p>
          </div>

          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-deep-sea-green" />
            <p className="text-sm text-[#555] text-center">
              A prompt should appear asking you to authenticate. If you don&apos;t
              see it, make sure you have a passkey registered on this device.
            </p>
          </div>
        </>
      )}

      {/* Success Step */}
      {step === 'success' && (
        <>
          <div className="flex flex-col gap-4">
            <h1 className="font-heading text-[28px] leading-8 text-black">
              Setup Complete!
            </h1>
            <p className="text-sm leading-5 text-black">
              Passkey authentication has been successfully configured.
            </p>
          </div>

          <div className="flex justify-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckIcon className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </>
      )}

      {/* Error Step */}
      {step === 'error' && (
        <>
          <div className="flex flex-col gap-4">
            <h1 className="font-heading text-[28px] leading-8 text-black">
              Setup Failed
            </h1>
          </div>

          <div className="flex flex-col gap-4">
            <div className="p-3 bg-red-50 border border-red-200 rounded">
              <p className="text-sm text-red-800">{error}</p>
            </div>

            <div className="flex items-center gap-3">
              <Button variant="primary" onClick={() => handleRegister(lastMethod!)}>
                Try Again
              </Button>
              <button
                type="button"
                onClick={() => setStep('intro')}
                className="text-sm font-medium text-[#555] hover:text-black transition-colors"
              >
                Choose a different method
              </button>
              {!isRequired && (
                <button
                  type="button"
                  onClick={onCancel}
                  className="text-sm font-medium text-[#555] hover:text-black transition-colors"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </AuthLayout>
  );
}
